import {
  compareWpmCheckIn,
  formatAccDeltaPp,
  formatWpmDelta,
} from '../lib/wpmCheckIn';
import type { BaselineSnapshot, WpmCheckIn } from '../types';

interface WpmCheckInSummaryProps {
  checkIn: WpmCheckIn;
  previous?: WpmCheckIn;
  baseline?: BaselineSnapshot;
  onContinue: () => void;
}

export default function WpmCheckInSummary({
  checkIn,
  previous,
  baseline,
  onContinue,
}: WpmCheckInSummaryProps) {
  const cmp = compareWpmCheckIn(checkIn, previous, baseline);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
      <header className="text-center">
        <p className="label text-emerald-700 dark:text-emerald-400">
          Hastighedstest gennemført
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          {checkIn.wpm.toFixed(0)} wpm
        </h1>
        <p className="mt-2 text-sm text-muted">
          {(checkIn.accuracy * 100).toFixed(0)}% nøjagtighed ·{' '}
          {checkIn.solidKeyCount} taster (solidt repertoire)
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <CompareCard
          title="Siden sidste test"
          wpmDelta={cmp.vsPreviousWpm}
          accDeltaPp={cmp.vsPreviousAccPp}
          reference={
            previous
              ? `${previous.wpm.toFixed(0)} wpm · ${(previous.accuracy * 100).toFixed(0)}%`
              : undefined
          }
          emptyHint="Din første officielle hastighedstest — næste gang kan du sammenligne."
        />
        <CompareCard
          title="Vs. udgangspunkt"
          subtitle="Afskrift ved onboarding"
          wpmDelta={cmp.vsBaselineWpm}
          accDeltaPp={cmp.vsBaselineAccPp}
          reference={
            baseline
              ? `${baseline.wpm.toFixed(0)} wpm · ${(baseline.accuracy * 100).toFixed(0)}%`
              : undefined
          }
          emptyHint="Du har ikke en gemt afskrift-baseline endnu."
        />
      </div>

      <InsightLine cmp={cmp} />

      <button
        type="button"
        onClick={onContinue}
        className="w-full rounded-xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400"
      >
        Tilbage til dashboard
      </button>
    </div>
  );
}

function CompareCard({
  title,
  subtitle,
  wpmDelta,
  accDeltaPp,
  reference,
  emptyHint,
}: {
  title: string;
  subtitle?: string;
  wpmDelta: number | null;
  accDeltaPp: number | null;
  reference?: string;
  emptyHint: string;
}) {
  const hasCompare = wpmDelta !== null;

  return (
    <div className="surface p-4 text-center">
      <p className="text-sm font-semibold text-strong">{title}</p>
      {subtitle && <p className="mt-0.5 text-xs text-faint">{subtitle}</p>}
      {hasCompare ? (
        <>
          <p
            className={`mt-3 font-mono text-2xl font-semibold ${
              (wpmDelta ?? 0) > 0
                ? 'text-emerald-700 dark:text-emerald-400'
                : (wpmDelta ?? 0) < 0
                  ? 'text-rose-700 dark:text-rose-400'
                  : 'text-strong'
            }`}
          >
            {formatWpmDelta(wpmDelta)}
          </p>
          <p className="mt-1 text-xs text-muted">
            Nøjagtighed: {formatAccDeltaPp(accDeltaPp)}
          </p>
          {reference && (
            <p className="mt-2 text-[10px] text-faint">Før: {reference}</p>
          )}
        </>
      ) : (
        <p className="mt-3 text-sm text-muted">{emptyHint}</p>
      )}
    </div>
  );
}

function InsightLine({ cmp }: { cmp: ReturnType<typeof compareWpmCheckIn> }) {
  if (cmp.vsPreviousWpm === null && cmp.vsBaselineWpm === null) {
    return (
      <p className="text-center text-sm text-muted">
        God start — tag testen igen om en uge for at se udviklingen.
      </p>
    );
  }

  const beatsBaseline =
    cmp.vsBaselineWpm !== null && cmp.vsBaselineWpm > 0;
  const beatsPrevious =
    cmp.vsPreviousWpm !== null && cmp.vsPreviousWpm > 0;

  if (beatsPrevious && beatsBaseline) {
    return (
      <p className="text-center text-sm text-emerald-800 dark:text-emerald-300">
        Du er hurtigere end både sidste test og dit udgangspunkt — flot fremgang.
      </p>
    );
  }
  if (beatsPrevious) {
    return (
      <p className="text-center text-sm text-muted">
        Bedre end sidste test. Hold kadencen — næste uge tæller igen.
      </p>
    );
  }
  if (beatsBaseline) {
    return (
      <p className="text-center text-sm text-muted">
        Du ligger over dit oprindelige udgangspunkt ved afskrift.
      </p>
    );
  }
  if (cmp.vsPreviousWpm !== null && cmp.vsPreviousWpm < 0) {
    return (
      <p className="text-center text-sm text-muted">
        Lidt lavere end sidst — det sker. Ét dårligt løb ændrer ikke trenden.
      </p>
    );
  }

  return null;
}
